import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { tickerDetailService } from '../../services/tickerDetailService';
import { SectionSkeleton, SectionError } from './CompanyProfile';
import { DividendHistoryChart } from './DividendHistoryChart';

interface Props { symbol: string; }

export function Dividends({ symbol }: Props): React.ReactElement {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['tickerDividends', symbol],
    queryFn: () => tickerDetailService.getDividends(symbol),
    staleTime: 24 * 60 * 60 * 1000,
    enabled: !!symbol,
  });

  if (isLoading) return <SectionSkeleton />;
  if (isError) return <SectionError message="Failed to load dividends" />;

  const dividends = data?.data?.dividends ?? [];
  const splits = data?.data?.splits ?? [];
  const recentDividends = dividends.slice(0, 12);

  return (
    <section className="p-4 border-b border-dark-border">
      <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3">
        Dividends &amp; Splits
      </h2>

      {/* Chart first — whether the payout grew, stalled or was cut is the
          question the table makes you reconstruct row by row. Splits ride on
          the same time axis, since their job here is to explain a step. */}
      <DividendHistoryChart dividends={dividends} splits={splits} />

      {/* Dividends table */}
      {recentDividends.length === 0 ? (
        <p className="text-gray-400 text-sm mb-4">No dividend history</p>
      ) : (
        <details className="mt-3 mb-6 group">
          <summary className="cursor-pointer text-xs text-gray-400 hover:text-gray-200 select-none">
            <span className="group-open:hidden">Show dividend dates</span>
            <span className="hidden group-open:inline">Hide dividend dates</span>
          </summary>
          <div className="overflow-x-auto mt-2">
          <table className="w-full text-xs text-left border-collapse">
            <thead>
              <tr className="border-b border-dark-border">
                {['Ex-Date', 'Dividend', 'Payment Date', 'Declaration Date'].map((h) => (
                  <th key={h} className="py-1.5 pr-6 text-gray-400 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentDividends.map((r, i) => (
                <tr key={i} className="border-b border-dark-border/50 hover:bg-dark-surface/50">
                  <td className="py-1.5 pr-6 text-white font-numeric">{r.date}</td>
                  <td className="py-1.5 pr-6 text-white font-numeric">${r.dividend?.toFixed(4)}</td>
                  <td className="py-1.5 pr-6 text-gray-400 font-numeric">{r.paymentDate || '\u2014'}</td>
                  <td className="py-1.5 pr-6 text-gray-400 font-numeric">{r.declarationDate || '\u2014'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </details>
      )}

      {/* Splits table */}
      {splits.length > 0 && (
        <details className="group">
          <summary className="cursor-pointer text-xs text-gray-400 hover:text-gray-200 select-none">
            <span className="group-open:hidden">Show split history</span>
            <span className="hidden group-open:inline">Hide split history</span>
          </summary>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-xs text-left border-collapse">
              <thead>
                <tr className="border-b border-dark-border">
                  {['Date', 'Ratio'].map((h) => (
                    <th key={h} className="py-1.5 pr-6 text-gray-400 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {splits.map((s, i) => (
                  <tr key={i} className="border-b border-dark-border/50 hover:bg-dark-surface/50">
                    <td className="py-1.5 pr-6 text-white font-numeric">{s.date}</td>
                    <td className="py-1.5 pr-6 text-gray-200 font-numeric">{s.numerator}/{s.denominator}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </section>
  );
}
